import * as Sentry from '@sentry/react';
import type { DriverState } from '@playcastdotio/CoreTypes';
import type { DeviceConfigManager } from './DeviceConfigManager';
import { resolveShadowcamDevice, getPluggedShadowcamSlotKeys } from './ShadowcamCaptureResolver';

const SHADOWCAM_DIAG = '[BLUE-STREAM-12010:SessionCast]';

// Default capture resolution for matchmaking-v2 per-session ShadowCam capture. The host requesting an
// exact size drives Playjector to render the instance's virtual monitor at exactly this resolution (see
// the capture-resolution note in api/stores/shared.ts); 1280x720 is a ShadowCam DirectShow catalog size,
// so `exact` negotiates cleanly. Per-campaign overrides can replace this constant later.
const DEFAULT_CAPTURE_RESOLUTION = { width: 1280, height: 720 } as const;

type DriverStateLike = DriverState | Record<string, boolean> | undefined;

export type SessionCastBinding = {
  shadowCamName?: string;
  audioDeviceName?: string;
};

export type SessionAudioStatus =
  | { status: 'ready'; track: MediaStreamTrack }
  | { status: 'DRIVER_BLOCKED'; track: null; reason: string };

export type SessionCaptureReadiness =
  | { status: 'ready' }
  | { status: 'DRIVER_BLOCKED'; reason: string };

export type SessionCastResult = {
  videoTrack: MediaStreamTrack | null;
  audio: SessionAudioStatus;
  readiness: SessionCaptureReadiness;
};

export class SessionCastManager {
  private readonly videoTracks = new Map<string, MediaStreamTrack>();
  private readonly audioStatuses = new Map<string, SessionAudioStatus>();

  constructor(
    private readonly deviceConfig: DeviceConfigManager,
    private readonly getDriverState: () => DriverStateLike,
  ) {}

  public getVideoTrackForSession(sessionId: string): MediaStreamTrack | null {
    return this.getLiveTrack(this.videoTracks.get(sessionId));
  }

  public getAudioTrackForSession(sessionId: string): MediaStreamTrack | null {
    const status = this.audioStatuses.get(sessionId);
    if (status?.status !== 'ready') return null;
    return this.getLiveTrack(status.track);
  }

  public getAudioStatusForSession(sessionId: string): SessionAudioStatus | undefined {
    return this.audioStatuses.get(sessionId);
  }

  public async startCastForSession(
    sessionId: string,
    binding: SessionCastBinding,
  ): Promise<SessionCastResult> {
    const videoTrack = binding.shadowCamName
      ? await this.startVideoForSession(sessionId, binding.shadowCamName)
      : this.getVideoTrackForSession(sessionId);
    const audio = binding.audioDeviceName
      ? await this.startAudioForSession(sessionId, binding.audioDeviceName)
      : this.reportBlockedAudio(sessionId);
    return {
      videoTrack,
      audio,
      readiness: videoTrack
        ? { status: 'ready' }
        : {
            status: 'DRIVER_BLOCKED',
            reason: binding.shadowCamName
              ? `ShadowCam "${binding.shadowCamName}" is unavailable for session ${sessionId}`
              : `No ShadowCam is configured for session ${sessionId}`,
          },
    };
  }

  public async startVideoForSession(
    sessionId: string,
    hubRoute: string,
  ): Promise<MediaStreamTrack | null> {
    Sentry.logger.info(
      `${SHADOWCAM_DIAG} startVideoForSession session=${sessionId} hubRoute=${hubRoute}`,
    );
    Sentry.addBreadcrumb({
      category: 'BLUE-STREAM-12010',
      message: 'startVideoForSession requested',
      level: 'info',
      data: { sessionId, hubRoute },
    });

    const existing = this.getVideoTrackForSession(sessionId);
    if (existing) {
      Sentry.logger.info(
        `${SHADOWCAM_DIAG} reusing existing live track for session=${sessionId} hubRoute=${hubRoute} ` +
        `(label="${existing.label}" readyState=${existing.readyState})`,
      );
      return existing;
    }

    await this.deviceConfig.enumerateDevices();

    // Snapshot what enumeration surfaced + which Windows slots PlayJector reports plugged, so a
    // resolution miss can be diagnosed as "device never enumerated" vs "resolver refused to guess"
    // (multiple plugged, no direct label match) — the two BLUE-STREAM-12010 no-video root causes.
    const shadowcams = this.deviceConfig.getShadowcams();
    const availableLabels = shadowcams.map((sc) => sc.displayName);
    const pluggedSlots = getPluggedShadowcamSlotKeys(this.getDriverState());

    const target = resolveShadowcamDevice(hubRoute, shadowcams, this.getDriverState());
    if (!target) {
      console.warn(
        `${SHADOWCAM_DIAG} resolveShadowcamDevice MISS session=${sessionId} hubRoute=${hubRoute} ` +
        `enumerated=[${availableLabels.join(', ')}] plugged=[${pluggedSlots.join(', ')}]`,
      );
      Sentry.captureMessage(
        `${SHADOWCAM_DIAG} ShadowCam "${hubRoute}" did not resolve to an enumerated device`,
        {
          level: 'error',
          tags: { shadowcam_diag: 'BLUE-STREAM-12010', shadowcam_stage: 'resolve_device' },
          fingerprint: ['shadowcam-resolve-miss', hubRoute],
          extra: {
            sessionId,
            hubRoute,
            enumeratedShadowcams: availableLabels,
            enumeratedCount: shadowcams.length,
            pluggedSlots,
            pluggedCount: pluggedSlots.length,
            // The resolver returns null specifically when >1 plugged and no direct label match.
            likelyReason:
              shadowcams.length === 0
                ? 'no shadowcams enumerated (stale Chromium cache or permission gap)'
                : pluggedSlots.length > 1
                  ? 'multiple slots plugged and no enumerated label matched hubRoute'
                  : 'hubRoute not found among enumerated labels',
          },
        },
      );
      return null;
    }

    Sentry.logger.info(
      `${SHADOWCAM_DIAG} resolved hubRoute=${hubRoute} -> device="${target.displayName}" ` +
      `deviceId=${target.deviceId ? target.deviceId.slice(0, 8) : 'no-id'} — calling getUserMedia`,
    );

    const { width, height } = DEFAULT_CAPTURE_RESOLUTION;
    let stream: MediaStream;
    try {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: target.deviceId },
            width: { exact: width },
            height: { exact: height },
          },
        });
      } catch (constraintError) {
        // A ShadowCam that can't serve the exact catalog size (unexpected, but possible mid-resize or
        // for a non-standard override) rejects with OverconstrainedError. Fall back to `ideal` so we
        // still get a track rather than failing the session; any other error propagates to the handler.
        if ((constraintError as DOMException)?.name === 'OverconstrainedError') {
          console.warn(
            `${SHADOWCAM_DIAG} ${target.displayName} cannot open at exact ${width}x${height}; retrying with ideal`,
            constraintError,
          );
          stream = await navigator.mediaDevices.getUserMedia({
            video: {
              deviceId: { exact: target.deviceId },
              width: { ideal: width },
              height: { ideal: height },
            },
          });
        } else {
          throw constraintError;
        }
      }
    } catch (error) {
      // Device existed at resolve time but getUserMedia rejected (gone/busy/permission) — a real
      // race given ShadowCam pool plug timing. Log with full context; rethrow to preserve behavior.
      console.error(
        `${SHADOWCAM_DIAG} getUserMedia THREW session=${sessionId} hubRoute=${hubRoute} ` +
        `device="${target.displayName}":`,
        error,
      );
      Sentry.captureException(error, {
        tags: { shadowcam_diag: 'BLUE-STREAM-12010', shadowcam_stage: 'get_user_media' },
        fingerprint: ['shadowcam-getusermedia-throw', hubRoute],
        extra: {
          sessionId,
          hubRoute,
          resolvedDevice: target.displayName,
          enumeratedShadowcams: availableLabels,
          pluggedSlots,
        },
      });
      throw error;
    }

    const track = stream.getVideoTracks()[0] ?? null;
    if (track) {
      Sentry.logger.info(
        `${SHADOWCAM_DIAG} getUserMedia SUCCESS session=${sessionId} hubRoute=${hubRoute} ` +
        `track(label="${track.label}" readyState=${track.readyState} enabled=${track.enabled} muted=${track.muted})`,
      );
      this.rememberTrack(this.videoTracks, sessionId, track);
    } else {
      console.warn(
        `${SHADOWCAM_DIAG} getUserMedia returned a stream with NO video track session=${sessionId} hubRoute=${hubRoute}`,
      );
      Sentry.captureMessage(
        `${SHADOWCAM_DIAG} getUserMedia returned no video track for "${hubRoute}"`,
        {
          level: 'error',
          tags: { shadowcam_diag: 'BLUE-STREAM-12010', shadowcam_stage: 'get_user_media' },
          fingerprint: ['shadowcam-getusermedia-no-track', hubRoute],
          extra: { sessionId, hubRoute, resolvedDevice: target.displayName },
        },
      );
    }
    return track;
  }

  public async startAudioForSession(
    sessionId: string,
    audioDeviceName: string,
  ): Promise<SessionAudioStatus> {
    const existing = this.getAudioTrackForSession(sessionId);
    if (existing) return { status: 'ready', track: existing };
    const devices = await navigator.mediaDevices.enumerateDevices();
    const target = devices.find(
      (device) => device.kind === 'audioinput' && device.label === audioDeviceName,
    );
    if (!target) {
      return this.reportBlockedAudio(
        sessionId,
        `Explicit AudioCaster endpoint "${audioDeviceName}" is unavailable`,
      );
    }
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: { exact: target.deviceId },
        channelCount: 2,
        autoGainControl: false,
        noiseSuppression: false,
        echoCancellation: false,
      },
    });
    const track = stream.getAudioTracks()[0];
    if (!track) {
      return this.reportBlockedAudio(
        sessionId,
        `Explicit AudioCaster endpoint "${audioDeviceName}" produced no track`,
      );
    }
    const status: SessionAudioStatus = { status: 'ready', track };
    this.audioStatuses.set(sessionId, status);
    track.addEventListener('ended', () => {
      if (this.getAudioTrackForSession(sessionId) === track) {
        this.audioStatuses.delete(sessionId);
      }
    });
    return status;
  }

  public stopCastForSession(sessionId: string): void {
    this.videoTracks.get(sessionId)?.stop();
    const audio = this.audioStatuses.get(sessionId);
    if (audio?.status === 'ready') audio.track.stop();
    this.videoTracks.delete(sessionId);
    this.audioStatuses.delete(sessionId);
  }

  public stopAllSessions(): void {
    for (const sessionId of new Set([
      ...this.videoTracks.keys(),
      ...this.audioStatuses.keys(),
    ])) {
      this.stopCastForSession(sessionId);
    }
  }

  private reportBlockedAudio(sessionId: string, reason?: string): SessionAudioStatus {
    const status: SessionAudioStatus = {
      status: 'DRIVER_BLOCKED',
      track: null,
      reason: reason ?? `No explicit per-session AudioCaster endpoint for ${sessionId}`,
    };
    this.audioStatuses.set(sessionId, status);
    return status;
  }

  private rememberTrack(
    tracks: Map<string, MediaStreamTrack>,
    sessionId: string,
    track: MediaStreamTrack,
  ): void {
    tracks.set(sessionId, track);
    track.addEventListener('ended', () => {
      if (tracks.get(sessionId) === track) tracks.delete(sessionId);
    });
  }

  private getLiveTrack(track: MediaStreamTrack | undefined): MediaStreamTrack | null {
    return track && track.readyState !== 'ended' ? track : null;
  }
}
